import React from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import CssBaseline from "@mui/material/CssBaseline";
import SaveIcon from "@mui/icons-material/Save";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import CurrencyInput from "../common/CurrencyInput";
import { connect } from "react-redux";
import FormHelperText from "@mui/material/FormHelperText";
import * as premiumUtils from "./PremiumUtils";
import { FormControl, InputAdornment } from "@mui/material";
import { withParams } from "../common/WithParams";

class PremiumDetailUser extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      user: {},
      membershiptype: props.type,
      memberstatus: "",
      premium: {},
      allPremiums: [],
      ownerships: [],
      error: "",
      insurancetype: "",
      inputDisabled: false,
      tffCodes: [],
      selectedTffCode: {},
      reportedByCompanyName: ""
    };
    this.currencyInputRef = React.createRef();
  }

  async componentDidMount() {
    await this.fetchData();
  }

  componentDidUpdate(prevProps) {
    const prevYear = prevProps.params?.year;
    const currentYear = this.props.params?.year;

    const typeChanged = prevProps.type !== this.props.type;
    const yearChanged = prevYear !== currentYear;

    if (typeChanged || yearChanged) {
      this.setState({ membershiptype: this.props.type }, async () => {
        await this.fetchData();
      });
    }
  }


  async fetchData() {
    try {
      const user = this.props.signedinuser;
      const year = this.props.params.year;
      if (!user) return;

      this.setState({ user });

      const company = await premiumUtils.fetchCompany(user.companyId);
      const tffCodes = company?.codeTFF || [];

      let mainTffCode = tffCodes.find(c => c.mainCode)?.code;
      if (this.state.membershiptype === "TFF" && !mainTffCode && tffCodes.length > 0) {
        mainTffCode = tffCodes[0].code;
        console.log("Fallback to first TFF code:", mainTffCode);
      }

      const existingPremiumData = await premiumUtils.fetchExistingPremium(
        this.state.membershiptype,
        year || "",
        user.companyId,
        this.state.membershiptype === "TFF" ? mainTffCode : undefined
      );

      const {
        existingPremium,
        prizeId,
        existingAmount,
        relevantYear,
        ownerships,
        allPremiums
      } = existingPremiumData || {};

      this.setState({
        premium: existingPremium
          ? {
            prizeId: prizeId,
            amount: existingAmount ?? "",
            prizeYear: relevantYear,
            orgMemId: company?.companyId,
            reportedCodeTff: mainTffCode || tffCodes[0]?.code,
          }
          : premiumUtils.setDefaultPremium(company),
        allPremiums,
        reportedByCompanyName: company?.companyName || "Unknown",
        ownerships: ownerships || [],
        inputDisabled: !existingPremium,
        memberstatus: company
          ? this.state.membershiptype === "YFF"
            ? company.yffStatusName
            : company.tffStatusName
          : "",
        insurancetype:
          this.state.membershiptype === "YFF"
            ? "yrkesskadeforsikring"
            : "trafikkforsikring",
        tffCodes: company.codeTFF
      });
    } catch (error) {
      console.error("Error in fetchData", error);
    }
  };

  onChange = (e) => {
    const { name, value } = e.target;
    const { allPremiums, premium } = this.state;

    const updatedPremium = { ...premium, [name]: value };

    if (name === "reportedCodeTff") {
      const match = allPremiums.find(p => Number(p.codeTFF) === Number(value));

      if (match) {
        updatedPremium.amount = match.amount;
        updatedPremium.prizeId = match.prizeId;
        updatedPremium.prizeYear = match.prizeYear;
        updatedPremium.locked = match.locked;
      } else {
        updatedPremium.amount = -1;
        updatedPremium.prizeId = -1;
        updatedPremium.locked = true;
      }
    }
    if (name === "amount") {
      updatedPremium.amount = value;
    }
    this.setState({ premium: updatedPremium });
  };

  onSave = async (event) => {
    event.preventDefault();

    const { valid, error, newAmount } = premiumUtils.formIsValid(
      this.state.premium,
      "update"
    );
    await this.setState({ error });
    if (!valid) return;

    // eslint-disable-next-line react/no-direct-mutation-state
    this.state.premium.amount = newAmount;

    const updatedPremium = {
      ...this.state.premium,
      reportedCodeTff: parseInt(this.state.premium.reportedCodeTff, 10),
      amount: newAmount,
    };

    const saveSuccess = await premiumUtils.savePremium(
      updatedPremium,
      "update"
    );
    if (saveSuccess) {
      await this.fetchData();
    } else {
      console.error("Update operation failed.");
    }
  };

  handleTransferPremium = async (prizeId) => {
    const targetCode = this.state.selectedTffCode[prizeId];
    if (!targetCode) {
      console.warn("Ingen TFF-kode valgt for overføring.");
      return;
    }

    const success = await premiumUtils.transferPremiumToCodeAndHandle(prizeId, this.state.premium.prizeYear, targetCode);

    if (success) {
      await this.fetchData();
    }
  };

  getAmountInt = () => {
    const raw = this.state.premium.amount;
    if (typeof raw !== "string") return parseInt(raw || 0, 10);
    return parseInt(raw.replace(/\s/g, ""), 10);
  };

  render() {
    const root = {
      width: "100%",
    };

    const layout = {
      width: "100%",
      paddingTop: 80,
    };

    const paper = {
      paddingLeft: 30,
      paddingTop: 20,
      paddingRight: 30,
      paddingBottom: 50,
    };

    const gridPaddingTop = {
      paddingTop: 20,
    };

    const head = {
      backgroundColor: "#eeeeee",
      fontWeight: "bold",
      fontSize: 16,
      textDecoration: "none",
    };

    const emptyPaper = {
      textAlign: "center",
      paddingLeft: 250,
    };

    const smallLabelStyle = {
      fontSize: "12px",
      color: "rgba(0, 0, 0, 0.6)",
    };

    const NOKPrefix = (
      <span
        style={{
          fontSize: "16px",
        }}
      >
        NOK
      </span>
    );

    const formcontrolselect = {
      minWidth: "100%",
    };
    const hasMarketShareOnly = this.state.allPremiums?.some(p => p.amount < 0);

    return (
      <Container maxWidth="lg" sx={{ ml: 0 }}>
        <form onSubmit={this.onSave}>
          <main style={layout}>
            <Paper style={paper}>
              <CssBaseline />
              <Grid container spacing={4}>
                <Grid item lg={5}>
                  <h2 style={{ marginBottom: "0px" }}>
                    {this.props.params.year
                      ? `Registrert ${this.state.membershiptype} Premie for ${this.props.params.year}`
                      : `Sist registrerte ${this.state.membershiptype === "TFF" ? "TFF" : "YFF"}-premie`}
                  </h2>
                  <Grid style={gridPaddingTop}>
                    <FormControl fullWidth>
                      <TextField
                        label="Medlem"
                        variant="standard"
                        value={`${this.state.reportedByCompanyName} (${this.state.membershiptype})`}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        disabled={this.state.premium.prizeYear < 0}
                      />
                    </FormControl>
                  </Grid>

                  {this.state.membershiptype === "TFF" && (
                    <Grid style={gridPaddingTop}>
                      <FormControl style={formcontrolselect} fullWidth>
                        {this.state.tffCodes.length === 1 ? (
                          <TextField
                            name="reportedCodeTff"
                            label="TFF Kode"
                            value={this.state.tffCodes[0]?.code || ""}
                            fullWidth
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        ) : (
                          <TextField
                            select
                            name="reportedCodeTff"
                            label="TFF Kode"
                            value={this.state.premium.reportedCodeTff ?? ""}
                            onChange={this.onChange}
                            fullWidth
                            variant="standard"
                            SelectProps={{
                              native: true,
                            }}
                          >
                            {this.state.tffCodes.map((codeObj) => (
                              <option key={codeObj.code} value={codeObj.code}>
                                {codeObj.code} {codeObj.mainCode ? "(Hovedkode)" : ""}
                              </option>
                            ))}
                          </TextField>
                        )}
                      </FormControl>
                      {(() => {
                        const mainCodeCount = this.state.tffCodes.filter(c => c.mainCode).length;

                        if (mainCodeCount > 1) {
                          return (
                            <Typography variant="body2" color="error" style={{ paddingTop: 8 }}>
                              Mer enn én hovedkode registrert, handling kreves
                            </Typography>
                          );
                        }

                        if (mainCodeCount === 0 && this.state.tffCodes.length > 0) {
                          return (
                            <Typography variant="body2" color="error" style={{ paddingTop: 8 }}>
                              Ingen hovedkode registrert, handling kreves
                            </Typography>
                          );
                        }

                        return null;
                      })()}
                      <FormHelperText>Velg TFF kode å registrere premie for</FormHelperText>
                    </Grid>
                  )}

                  <Grid style={gridPaddingTop}>
                    <FormControl fullWidth>
                      <TextField
                        label="År"
                        variant="standard"
                        value={
                          this.state.premium.prizeYear < 0
                            ? "Ingen tidligere premie"
                            : this.state.premium.prizeYear || ""
                        }
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        disabled={this.state.premium.prizeYear < 0}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item style={gridPaddingTop}>
                    <FormControl fullWidth className="MuiFormControl-root MuiTextField-root">
                      <Typography variant="caption" style={smallLabelStyle}>
                        Opptjent premie
                      </Typography>
                      <div>
                        <CurrencyInput
                          ref={this.currencyInputRef}
                          name="amount"
                          InputProps={{
                            startAdornment:
                              !hasMarketShareOnly &&
                                this.getAmountInt() >= 0 &&
                                this.state.premium.amount !== undefined &&
                                this.state.premium.amount !== null ? (
                                <InputAdornment position="start">{NOKPrefix}</InputAdornment>
                              ) : null,
                          }}
                          placeholder={
                            hasMarketShareOnly
                              ? "Ingen registrert premie, kun markedsandel"
                              : this.getAmountInt() < 0
                                ? "Ingen tidligere premier"
                                : ""
                          }
                          type="text"
                          value={
                            this.state.premium.amount !== undefined &&
                              this.state.premium.amount !== null &&
                              this.getAmountInt() >= 0
                              ? String(this.state.premium.amount)
                              : ""
                          }
                          onChange={this.onChange}
                          disabled={
                            this.state.inputDisabled ||
                            this.state.premium.locked ||
                            this.state.memberstatus !== "Aktiv" ||
                            this.getAmountInt() < 0 ||
                            hasMarketShareOnly
                          }
                          style={{
                            position: "relative",
                            top: "1px",
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormHelperText>
                      {`Opptjent premie i ${this.getAmountInt() === -1
                        ? "foregående år"
                        : this.state.premium.prizeYear || "foregående år"
                        } for lovpålagt 
                          ${this.state.insurancetype} (i hele norske kroner). 
                          Direkte norsk forretning uten fradrag av reassuranse${this.state.membership === "TFF" ? " og uten TFA" : ""
                        }.`}
                    </FormHelperText>
                    <Typography style={{ color: "red" }}>
                      {this.state.error.amount}
                    </Typography>
                  </Grid>

                  <Grid style={{ paddingTop: 40 }}>
                    <Button
                      startIcon={<SaveIcon />}
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      Lagre
                    </Button>
                  </Grid>
                </Grid>

                <Grid item lg={7}>
                  <h2>Opprinnelse og eierskap</h2>
                  <div style={gridPaddingTop}>
                    <Paper style={root}>
                      <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                          <TableRow>
                            <TableCell key="ownedByCompanyName" style={head}>
                              Navn
                            </TableCell>
                            <TableCell key="fromDate" style={head}>
                              Fra
                            </TableCell>
                            <TableCell key="toDate" style={head}>
                              Til
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {this.state.ownerships && this.state.ownerships.length > 0 ? (
                            this.state.ownerships.map((ownership, index) => {
                              const formatDate = (dateString) => {
                                if (!dateString) return "";
                                const date = new Date(dateString);
                                const day = String(date.getDate()).padStart(2, "0");
                                const month = String(date.getMonth() + 1).padStart(2, "0");
                                const year = date.getFullYear();
                                return `${day}.${month}.${year}`;
                              };

                              const fromDate = formatDate(ownership.fromDate);
                              const toDate = formatDate(ownership.toDate);

                              return (
                                <TableRow hover tabIndex={-1} key={index}>
                                  <TableCell key="ownedByCompanyName">
                                    <Typography>
                                      {ownership.ownedByCompanyName}
                                    </Typography>
                                  </TableCell>
                                  <TableCell key="fromDate">
                                    <Typography>{fromDate}</Typography>
                                  </TableCell>
                                  <TableCell key="toDate">
                                    <Typography>{toDate}</Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell>
                                <Typography style={emptyPaper}>
                                  Ingen eierskap
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  </div>
                </Grid>

                {this.state.membershiptype === "TFF" && (
                  <Grid item xs={12} style={gridPaddingTop}>
                    <h2>Tidligere registrerte premier</h2>
                    <Paper style={root}>
                      <Table stickyHeader aria-label="registered premiums table">
                        <TableHead>
                          <TableRow>
                            <TableCell style={head}>Rapportert av</TableCell>
                            <TableCell style={head}>TFF Kode</TableCell>
                            <TableCell style={head}>Handling</TableCell>
                            <TableCell style={head}>År</TableCell>
                            <TableCell style={head}>
                              {hasMarketShareOnly ? "Markedsandel" : "Beløp (NOK)"}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>

                          {this.state.allPremiums && this.state.allPremiums.length > 0 ? (

                            <>
                              {/* Total row */}
                              <TableRow>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  Total sum registrert for Risikobærer
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  -----
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  -----
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  {this.state.premium.prizeYear}
                                </TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>
                                  {this.state.allPremiums
                                    .filter(p => typeof p.amount === "number" && p.amount >= 0)
                                    .reduce((sum, p) => sum + p.amount, 0)
                                    .toLocaleString("nb-NO")}
                                </TableCell>
                              </TableRow>

                              {/* Individual premium rows */}
                              {[...this.state.allPremiums]
                                .sort((a, b) => Number(a.codeTFF) - Number(b.codeTFF))
                                .map((p) => {
                                  const availableCodes = this.state.tffCodes.filter(
                                    (codeObj) => Number(codeObj.code) !== Number(p.codeTFF)
                                  );

                                  return (
                                    <TableRow key={p.prizeId}>
                                      <TableCell>{p.reportedByCompanyName}</TableCell>
                                      <TableCell>
                                        {p.codeTFF}
                                        {this.state.tffCodes.find(
                                          (c) => Number(c.code) === Number(p.codeTFF) && c.mainCode
                                        )
                                          ? " (Hovedkode)"
                                          : ""}
                                      </TableCell>
                                      <TableCell>
                                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                          <Select
                                            value={this.state.selectedTffCode[p.prizeId] ?? ""}
                                            onChange={(e) => {
                                              const selectedValue = e.target.value;
                                              this.setState((prev) => ({
                                                selectedTffCode: {
                                                  ...prev.selectedTffCode,
                                                  [p.prizeId]: selectedValue,
                                                },
                                              }));
                                            }}
                                            displayEmpty
                                            variant="standard"
                                            native
                                            disabled={availableCodes.length === 0}
                                            style={{ minWidth: "100px" }}
                                          >
                                            <option value="">Velg kode</option>
                                            {this.state.tffCodes
                                              .filter((codeObj) => Number(codeObj.code) !== Number(p.codeTFF))
                                              .map((codeObj) => (
                                                <option key={codeObj.code} value={codeObj.code}>
                                                  {codeObj.code} {codeObj.mainCode ? "(Hovedkode)" : ""}
                                                </option>
                                              ))}
                                          </Select>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => this.handleTransferPremium(p.prizeId)}
                                            disabled={
                                              availableCodes.length === 0 || !this.state.selectedTffCode[p.prizeId]
                                            }
                                          >
                                            Overfør
                                          </Button>
                                        </div>
                                      </TableCell>
                                      <TableCell>{p.prizeYear}</TableCell>
                                      <TableCell>
                                        {p.amount >= 0
                                          ? Number(p.amount).toLocaleString("nb-NO")
                                          : p.percentShare != null
                                            ? `${(p.percentShare).toFixed(4).replace(".", ",")} %`
                                            : "N/A"}
                                      </TableCell>

                                    </TableRow>
                                  );
                                })}
                            </>
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                Ingen registrerte premier
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                )}

              </Grid>
            </Paper>
          </main>
        </form>
      </Container >
    );
  }
}

function mapStateToProps(state) {
  const signedinuser = state.signedinuserobject
    ? state.signedinuserobject.signedInUser
    : sessionStorage.getItem("token")
      ? JSON.parse(sessionStorage.getItem("token"))
      : [];

  return { signedinuser: signedinuser };
}

export default connect(mapStateToProps)(withParams(PremiumDetailUser));
